import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { PersonContactGridItemObject } from '@it2go/types';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { fb, isAllowed, translate } from '../../../../../shared/shared.global-service';
import {
    PersonContactUpsertDialogComponent,
} from '../../../../components/person/person-contact-upsert-dialog/person-contact-upsert-dialog.component';
import { BaseDialogService } from '../../../../../../components/kit/base-dialog/base-dialog-service';
import { ServiceTypeEnum } from '../../../../../../../../../types/src/services/enum/service-type.enum';
import { personContactFrag } from './person-contacts-page.query';
import { SwitchInputComponent } from '../../../../../../components/kit/input/switch-input/switch-input.component';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { ContactCardComponent } from '../../../../components/person/contact-card/contact-card.component';
import { AppFormGroup, changeSignal } from '../../../../../../utils/form.utils';
import { PersonContactsPageStore } from './person-contacts-page.store';
import { LoadingComponent } from '../../../../../../components/kit/loading/loading.component';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';

@Component({
    selector: 'app-person-contacts-page',
    templateUrl: 'person-contacts-page.component.html',
    styleUrls: ['person-contacts-page.component.scss'],
    providers: [PersonContactsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SwitchInputComponent,
        ButtonComponent,
        ContactCardComponent,
        LoadingComponent,
        EmptyStateComponent,
    ],
})
export class PersonContactsPageComponent {

    public readonly dialogService = inject(BaseDialogService);
    public readonly store = inject(PersonContactsPageStore);

    protected form = new AppFormGroup({
        active: fb.control(false),
    });

    private active = changeSignal(this.form.controls.active);

    private contactDragged: PersonContactGridItemObject | null = null;

    constructor() {
        this.store.onSuccess(() => this.fetchData(),
            'createPersonContact',
            'updatePersonContact',
            'deletePersonContact',
            'markPersonContactAsPrimary',
            'unmarkPrimaryPersonContact',
            'activatePersonContact',
            'deactivatePersonContact',
            'reorderPersonContactOrder',
            'updateUserWorkplace',
        );

        effect(() => {
            if (this.store.folder() && this.store.contactsSharingIds()) {
                this.fetchData(this.active());
            }
        });
    }

    dragStart(contact: PersonContactGridItemObject) {
        if (!isAllowed['folderContact.update']()) return;
        this.contactDragged = contact;
    }

    drop(order: number) {
        if (!isAllowed['folderContact.update']() || !this.store.folderId() || !this.contactDragged) return;
        this.store.api.reorderPersonContactOrder({
                folderId: this.store.folderId(),
                contactId: this.contactDragged.id,
                order,
            },
        ).then();
    }

    protected addContactDialog() {
        if (!isAllowed['folderContact.create']()) return;
        this.dialogService.open(
            PersonContactUpsertDialogComponent,
            {
                header: translate.instant('database.persons.contacts.addHeader'),
                styleClass: 'p-dialog-width-60',
                data: {},
            },
        );
    }

    private fetchData(active: boolean | null = null) {
        if (!this.store.folderId() || !this.store.contactsSharingIds()) return;
        const filter = new FilterBuilder();

        if (this.store.service?.type === ServiceTypeEnum.SOCIAL && this.store.folder()) {
            filter.where('folderIdsPersonId', [
                this.store.folderId(),
                ...this.store.contactsSharingIds()!, // TODO: BE si musí vytáhnout validní sharedId, nelze spoléhat, že mu to přijde z UI
                this.store.folder()!.person.id,
            ]);
        } else {
            filter.where('folderId', [this.store.folderId(), ...this.store.contactsSharingIds()!], 'IN');
        }
        filter.sort('primary', 'DESC');
        filter.sort('order');

        if (active === null) active = !!this.form.value.active;
        if (!active) {
            filter.where('active', 'true');
        }

        this.store.api.getPersonContacts(filter.singlePage(), personContactFrag).then();
    }

    protected readonly isAllowed = isAllowed;
}
